import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'react-apollo';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { injectIntl } from 'react-intl';
import moment from 'moment';
import cx from 'classnames';

import PlaceGeoSuggest from '../../Home/PlaceGeoSuggest';
import { setPersonalizedValues } from '../../../actions/personalized';
import history from '../../../core/history';
import messages from '../../../locale/messages';

import s from './SearchBar.css';
import btn from '../shared/landingButtons.css';

const ISO_DATE_FORMAT = 'YYYY-MM-DD';
const DISPLAY_DATE_FORMAT = 'DD/MM/YYYY';

const getTodayIsoDate = () => moment().format(ISO_DATE_FORMAT);

const isValidIsoDate = (value) => moment(value, ISO_DATE_FORMAT, true).isValid();

const formatIsoDateForDisplay = (isoDate) => {
  if (!isoDate) return '';
  const parsed = moment(isoDate, ISO_DATE_FORMAT, true);
  return parsed.isValid() ? parsed.format(DISPLAY_DATE_FORMAT) : '';
};

const getMinIsoDate = (field, startDate) => {
  const today = getTodayIsoDate();
  if (field === 'startDate') return today;
  return startDate && startDate >= today ? startDate : today;
};

const validateIsoDate = (value, field, startDate) => {
  if (!value) return '';
  if (!isValidIsoDate(value)) return '';
  if (value < getMinIsoDate(field, startDate)) return '';
  return value;
};

const clearEndDateIfBeforeStart = (startDate, endDate, setPersonalizedValues) => {
  if (startDate && endDate && endDate < startDate) {
    setPersonalizedValues({ name: 'endDate', value: null });
  }
};

const buildSearchUri = (personalized) => {
  let uri = '/s?';

  if (personalized?.chosen != null) {
    uri += `&address=${personalized.location}&chosen=${personalized.chosen}`;
  } else if (personalized?.location != null) {
    uri += `&address=${personalized.location}`;
  }

  if (personalized?.startDate != null && personalized?.endDate != null) {
    uri += `&startDate=${personalized.startDate}&endDate=${personalized.endDate}`;
  }

  return uri;
};

class SearchDateInput extends React.Component {
  static propTypes = {
    field: PropTypes.oneOf(['startDate', 'endDate']).isRequired,
    value: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    placeholder: PropTypes.string.isRequired,
    className: PropTypes.string,
    setPersonalizedValues: PropTypes.func.isRequired,
  };

  state = { isFocused: false };

  handleFocus = (e) => {
    const { field, startDate } = this.props;
    this.setState({ isFocused: true });
    e.target.min = getMinIsoDate(field, startDate);
  };

  handleChange = (e) => {
    const value = e.target.value || '';
    if (value && !isValidIsoDate(value)) return;

    const { field, setPersonalizedValues, endDate } = this.props;
    setPersonalizedValues({ name: field, value: value || null });

    if (field === 'startDate') {
      clearEndDateIfBeforeStart(value, endDate, setPersonalizedValues);
    }
  };

  handleBlur = (e) => {
    const { field, value, startDate, endDate, setPersonalizedValues } = this.props;
    const validated = validateIsoDate(e.target.value || '', field, startDate);

    if ((value || '') !== validated) {
      setPersonalizedValues({ name: field, value: validated || null });
    }

    if (field === 'startDate') {
      clearEndDateIfBeforeStart(validated, endDate, setPersonalizedValues);
    }

    this.setState({ isFocused: false });
  };

  render() {
    const { field, value, placeholder, className, startDate } = this.props;
    const isoValue = value || '';
    const { isFocused } = this.state;

    return (
      <input
        type={isFocused ? 'date' : 'text'}
        className={className}
        placeholder={placeholder}
        value={isFocused ? isoValue : formatIsoDateForDisplay(isoValue)}
        min={isFocused ? getMinIsoDate(field, startDate) : undefined}
        readOnly={!isFocused}
        onChange={this.handleChange}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
      />
    );
  }
}

class SearchBar extends React.Component {
  static propTypes = {
    personalized: PropTypes.shape({
      location: PropTypes.string,
      chosen: PropTypes.number,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
    }),
    setPersonalizedValues: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
  };

  handleClick = () => {
    history.push(encodeURI(buildSearchUri(this.props.personalized)));
  };

  render() {
    const { intl, personalized, setPersonalizedValues } = this.props;
    const startDate = personalized?.startDate;
    const endDate = personalized?.endDate;

    return (
      <div className={s.searchPillLarge}>
        <div className={s.searchInputGroup}>
          <div className={cx(s.searchField, s.searchInputWide)}>
            <PlaceGeoSuggest
              label={intl.formatMessage(messages.newLandingWherePlaceholder)}
              className={s.searchInputLarge}
              containerClassName={s.geoSuggestContainer}
            />
          </div>
          <div className={s.searchDividerLarge} />
          <SearchDateInput
            field="startDate"
            value={startDate}
            startDate={startDate}
            endDate={endDate}
            placeholder={intl.formatMessage(messages.newLandingPickupDate)}
            className={cx(s.searchInputLarge, s.searchInputNarrow)}
            setPersonalizedValues={setPersonalizedValues}
          />
          <div className={s.searchDividerLarge} />
          <SearchDateInput
            field="endDate"
            value={endDate}
            startDate={startDate}
            endDate={endDate}
            placeholder={intl.formatMessage(messages.newLandingReturnDate)}
            className={cx(s.searchInputLarge, s.searchInputNarrow)}
            setPersonalizedValues={setPersonalizedValues}
          />
        </div>
        <button
          type="button"
          onClick={this.handleClick}
          className={cx(btn.landingBtn, btn.landingBtnSearch, s.searchSubmit)}
        >
          {intl.formatMessage(messages.newLandingSearch)}
        </button>
      </div>
    );
  }
}

const mapState = (state) => ({
  personalized: state?.personalized,
});

const mapDispatch = {
  setPersonalizedValues,
};

export default compose(
  injectIntl,
  withStyles(s, btn),
  connect(mapState, mapDispatch),
)(SearchBar);
